import ExcelJS from "exceljs";
import { dayjs } from "@/core/utils/date";
import { FORMAT_FILENAME_DATE } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import { getUserFullName } from "@/core/utils/name";
import { SPIFF_RULE_EVENTS_SHORT_LABELS } from "@/spiff-rules/constants";
import { SpiffRuleEvent } from "@/spiff-rules/spiff-rule";
import { DATA_SHEET_HEADERS } from "@/spiffs/constants";
import { ISpiff } from "@/spiffs/spiffs.d";
import { EnrichedUserWithSpiffs } from "@/user/user";

/**
 * Converts enriched users data to CSV format for payroll period export
 * @param enrichedUsers - Array of enriched users with spiff counts
 * @returns 2D array of strings representing CSV rows (including headers)
 */
export const generatePayrollPeriodCsvData = (
  enrichedUsers: EnrichedUserWithSpiffs[],
): string[][] => {
  const headers = [...DATA_SHEET_HEADERS.PAYROLL_PERIOD];

  const rows = enrichedUsers.map((user) => [
    getUserFullName(user) || "Unknown",
    user.signup_count.toString(),
    user.referral_creation_count.toString(),
    user.referral_completion_count.toString(),
    user.five_star_review_count.toString(),
    user.yard_sign_photo_count.toString(),
    user.pricebook_item_sale_count.toString(),
    user.tech_generated_lead_count.toString(),
    user.driving_score_count.toString(),
    user.generic_count.toString(),
    formatMoney(user.total_amount) ?? "$0.00",
  ]);

  return [headers, ...rows];
};

/**
 * Generates a filename for payroll period CSV export
 * @param startDate - Start date of the payroll period
 * @param endDate - End date of the payroll period
 * @returns Filename string
 */
export const generatePayrollPeriodCsvFilename = (
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
): string =>
  `payroll-period-${startDate.format(FORMAT_FILENAME_DATE)}-${endDate.format(FORMAT_FILENAME_DATE)}.csv`;

/**
 * Generates a filename for payroll period Excel export
 * @param startDate - Start date of the payroll period
 * @param endDate - End date of the payroll period
 * @returns Filename string
 */
export const generatePayrollPeriodExcelFilename = (
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
): string =>
  `payroll-period-${startDate.format(FORMAT_FILENAME_DATE)}-${endDate.format(FORMAT_FILENAME_DATE)}.xlsx`;

/**
 * Extracts IDs from spiff trigger details
 * @param spiff - The spiff to extract IDs from
 * @returns Object with jobId and invoiceId as numbers or empty strings
 */
const getSpiffTriggerIds = (
  spiff: ISpiff,
): { jobId: number | string; invoiceId: number | string } => {
  if (!spiff.trigger_details) {
    return { jobId: "", invoiceId: "" };
  }

  return {
    jobId: spiff.trigger_details.job_id ?? "",
    invoiceId: spiff.trigger_details.invoice_id ?? "",
  };
};

/**
 * Generates Excel workbook with a sheet per technician containing their spiff line items
 * @param enrichedUsers - Array of enriched users with spiffs
 * @returns Excel workbook
 */
export const generatePayrollPeriodExcel = (
  enrichedUsers: EnrichedUserWithSpiffs[],
): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();

  const sheetNameCounts = new Map<string, number>();

  enrichedUsers.forEach((user) => {
    const activeSpiffs = user.spiffs.filter((spiff) => !spiff.dismissed_at);

    if (activeSpiffs.length === 0) {
      return;
    }

    const sanitized = getUserFullName(user)
      .substring(0, 31)
      .replace(/[:\\/?*[\]]/g, "")
      .trim();
    const safeBase =
      !sanitized || sanitized === "-" ? `User ${user.id}` : sanitized;
    const count = sheetNameCounts.get(safeBase) ?? 0;
    sheetNameCounts.set(safeBase, count + 1);
    const sanitizedSheetName =
      count === 0 ? safeBase : `${safeBase.substring(0, 28)} ${count + 1}`;
    const worksheet = workbook.addWorksheet(sanitizedSheetName);

    worksheet.columns = [
      { header: "Type", key: "type", width: 20 },
      { header: "Description", key: "description", width: 40 },
      { header: "Amount", key: "amount", width: 15 },
      { header: "Event Date", key: "eventDate", width: 15 },
      { header: "ServiceTitan Job ID", key: "jobId", width: 20 },
      { header: "ServiceTitan Invoice ID", key: "invoiceId", width: 20 },
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "ff153057" },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 30;

    activeSpiffs.forEach((spiff) => {
      const spiffType =
        SPIFF_RULE_EVENTS_SHORT_LABELS[spiff.trigger_event as SpiffRuleEvent] ||
        spiff.trigger_event;
      const description = spiff.display_identification || "";
      const amount = spiff.amount;
      const eventDate = dayjs(spiff.event_date).toDate();
      const { jobId, invoiceId } = getSpiffTriggerIds(spiff);

      const row = worksheet.addRow({
        type: spiffType,
        description,
        amount,
        eventDate,
        jobId,
        invoiceId,
      });

      row.getCell("amount").numFmt = "$#,##0.00";

      row.getCell("eventDate").numFmt = "mm/dd/yyyy";
    });

    const totalAmount = activeSpiffs.reduce(
      (sum, spiff) => sum + Number(spiff.amount || 0),
      0,
    );

    const totalRow = worksheet.addRow({
      type: "",
      description: "",
      amount: totalAmount,
      eventDate: "TOTAL",
      jobId: "",
      invoiceId: "",
    });

    totalRow.font = { bold: true };
    totalRow.getCell("amount").numFmt = "$#,##0.00";
  });

  return workbook;
};

/**
 * Downloads an Excel workbook
 * @param workbook - The workbook to download
 * @param filename - The filename for the download
 */
export const downloadExcel = async (
  workbook: ExcelJS.Workbook,
  filename: string,
) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
